import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IInputConstraint, IInputConstraintType, IInputOption, IInputRender, InputType } from 'libs/shared/src/models';
import { DateService } from 'libs/shared/src/services/date/date.service';

// Define the regex patterns for some input validation
export const InputValidationPatterns: Partial<Record<IInputConstraintType, RegExp>> = {
  InputConstraintEMail: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  InputConstraintOnlyLetters: /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/,
  InputConstraintContainsValidZIP: /^(?=.*\d)[0-9A-Za-z]{4,10}$/,
  InputConstraintPassword: /^(?=.*[A-Z])(?=.*\d).{8,}$/
};

@Injectable({
  providedIn: 'root'
})
export class InputValidationService {
  // Services
  private readonly translateService: TranslateService = inject(TranslateService);

  // Methods
  /**
   * Generates validators for a form control based on the input constraints.
   * @param formCtrl - The form control to which the validators will be applied.
   * @param input - The input metadata containing constraints.
   * @param formGroup - Optional form group for cross-field validation (e.g., repeat/confirmation fields).
   * This method sets the validators on the form control and updates its validity.
   */
  public generateValidators(formCtrl: AbstractControl<IInputOption | string | null>, input?: IInputRender, formGroup?: FormGroup): void {
    const validators: ValidatorFn[] = [];
    if (input?.constraints) {
      input.constraints.forEach((constraint) => {
        // Skip regex constraints for FileUpload inputs — file name validation is handled by the component
        if (input.inputType === InputType.FileUpload && constraint.type === 'InputConstraintRegex') return;
        const constraintValidators = this.getValidator(constraint, formGroup);
        if (Array.isArray(constraintValidators)) {
          validators.push(...constraintValidators);
        } else {
          validators.push(constraintValidators);
        }
      });
    }
    formCtrl.setValidators(validators);
    formCtrl.updateValueAndValidity();
  }

  /**
   * Returns the error message for a specific validation type by looking up the constraint in the input.
   * Used by the cloudfront validation-message component.
   * @param validationType - The type of validation error to retrieve the message for.
   * @param input - The input metadata containing constraints and messages.
   * @returns The error message for the validation type, or undefined if no message is defined.
   */
  public getErrorMessage(validationType: IInputConstraintType, input: IInputRender): string | undefined {
    const constraint = input.constraints.find((c) => c.type === validationType);
    return this.resolveConstraintMessage(validationType, constraint);
  }

  /**
   * Applies constraints as Angular validators to a FormControl without replacing existing validators.
   * Used by the schema-based form system (generateFormGroup) to apply backend constraints.
   * @param formCtrl - The form control to add validators to.
   * @param constraints - The constraints to apply.
   * @param formGroup - Optional form group for cross-field validation (e.g., repeat/confirmation fields).
   */
  public applyConstraints(formCtrl: AbstractControl, constraints: IInputConstraint[], formGroup?: FormGroup): void {
    const validators: ValidatorFn[] = [];
    constraints.forEach((constraint) => {
      const v = this.getValidator(constraint, formGroup);
      if (Array.isArray(v)) validators.push(...v);
      else validators.push(v);
    });
    if (validators.length > 0) {
      formCtrl.addValidators(validators);
      formCtrl.updateValueAndValidity();
    }
  }

  /**
   * Returns error messages for constraints that have matching Angular validation errors.
   * Works with IInputConstraint[] directly (no need for the full IInputRender object).
   * Used by both the GsForm schema system and the cloudfront validation-message component.
   * @param constraints - The constraints to check against.
   * @param errors - The Angular validation errors from a FormControl.
   * @returns An array of translated error messages for the failing constraints.
   */
  public getConstraintErrorMessages(constraints: IInputConstraint[], errors: Record<string, unknown>): string[] {
    if (!errors || !constraints?.length) return [];
    const messages: string[] = [];
    for (const constraint of constraints) {
      // LengthMinMax maps to both minlength and maxlength — check either
      if (constraint.type === 'InputConstraintLengthMinMax') {
        if (errors['minlength'] || errors['maxlength']) {
          const msg = this.resolveConstraintMessage(constraint.type, constraint);
          if (msg && !messages.includes(msg)) messages.push(msg);
        }
        continue;
      }
      // DateRange has directional sub-errors (dateRangeMin/dateRangeMax) that get appended
      // to the base message so the user knows whether the date is too early or too late.
      if (constraint.type === 'InputConstraintDateRange') {
        const msg = this.resolveDateRangeMessage(constraint, errors);
        if (msg && !messages.includes(msg)) messages.push(msg);
        continue;
      }
      const errorKey = this.constraintTypeToErrorKey(constraint.type);
      if (errorKey && errors[errorKey]) {
        const msg = this.resolveConstraintMessage(constraint.type, constraint);
        if (msg && !messages.includes(msg)) messages.push(msg);
      }
    }
    return messages;
  }

  /**
   * Resolves the validation message for an `InputConstraintDateRange`.
   * Combines the base message (backend-provided or generic fallback) with a directional hint
   * indicating whether the date is too early (`dateRangeMin`) or too late (`dateRangeMax`).
   * Returns undefined when no relevant error is present.
   * @param constraint - The DateRange constraint, used for an optional backend message.
   * @param errors - The form control's validation errors.
   * @returns The combined message, or undefined when no date-range error fired.
   */
  private resolveDateRangeMessage(constraint: IInputConstraint, errors: Record<string, unknown>): string | undefined {
    const hasMinError = !!errors['dateRangeMin'];
    const hasMaxError = !!errors['dateRangeMax'];
    const hasGenericError = !!errors['dateRange'];
    if (!hasMinError && !hasMaxError && !hasGenericError) return undefined;

    const directional = hasMinError
      ? this.translateService.instant('validation.dateRangeMin')
      : hasMaxError
        ? this.translateService.instant('validation.dateRangeMax')
        : undefined;

    if (constraint.message && directional) return `${constraint.message} ${directional}`;
    if (directional) return directional;
    return this.resolveConstraintMessage(constraint.type, constraint);
  }

  /**
   * Maps a constraint type to its corresponding Angular FormControl error key.
   * @param type - The constraint type to map.
   * @returns The Angular error key, or undefined if no mapping exists.
   */
  private constraintTypeToErrorKey(type: IInputConstraintType): string | undefined {
    const map: Partial<Record<IInputConstraintType, string>> = {
      InputConstraintRequired: 'required',
      InputConstraintMinLength: 'minlength',
      InputConstraintMaxLength: 'maxlength',
      InputConstraintEMail: 'email',
      InputConstraintMin: 'min',
      InputConstraintMax: 'max',
      InputConstraintRegex: 'pattern',
      InputConstraintOnlyLetters: 'pattern',
      InputConstraintContainsValidZIP: 'pattern',
      InputConstraintPassword: 'pattern',
      InputConstraintRepeat: 'repeat',
      InputConstraintDateRange: 'dateRange',
      InputConstraintBlock: 'blockRequired'
    };
    return map[type];
  }

  /**
   * Resolves the error message for a constraint type, using the constraint's custom message if available,
   * otherwise falling back to a translated default message.
   * Shared core logic used by both getErrorMessage() and getConstraintErrorMessages().
   * @param type - The constraint type.
   * @param constraint - Optional constraint object with custom message and parameters.
   * @returns The resolved error message string, or undefined for types without a default.
   */
  private resolveConstraintMessage(type: IInputConstraintType, constraint?: IInputConstraint): string | undefined {
    if (constraint?.message) return constraint.message;
    switch (type) {
      case 'InputConstraintRequired':
        return this.translateService.instant('validation.fieldRequired');
      case 'InputConstraintLengthMinMax':
        return this.translateService.instant('validation.minMaxLength', {
          minLength: constraint?.minLength,
          maxLength: constraint?.maxLength
        });
      case 'InputConstraintMinLength':
        if (!constraint?.minLength) return;
        return this.translateService.instant('validation.minLength', { minLength: constraint.minLength });
      case 'InputConstraintMaxLength':
        if (!constraint?.maxLength) return;
        return this.translateService.instant('validation.maxLength', { maxLength: constraint.maxLength });
      case 'InputConstraintRegex':
        return this.translateService.instant('validation.regex');
      case 'InputConstraintEMail':
        return this.translateService.instant('validation.email');
      case 'InputConstraintOnlyLetters':
        return this.translateService.instant('validation.onlyLetters');
      case 'InputConstraintContainsValidZIP':
        return this.translateService.instant('validation.zip');
      case 'InputConstraintMin':
        return this.translateService.instant('validation.min', { min: constraint?.min ?? 'not defined' });
      case 'InputConstraintMax':
        return this.translateService.instant('validation.max', { max: constraint?.max ?? 'not defined' });
      case 'InputConstraintPasswordMismatch':
        return this.translateService.instant('validation.passwordMismatch');
      case 'InputConstraintRepeat':
        return this.translateService.instant('validation.repeat');
      case 'InputConstraintDateRange':
        return this.translateService.instant('validation.dateRange');
      case 'InputConstraintBlock':
        return this.translateService.instant('validation.blockRequired');
      default:
        return this.translateService.instant('validation.invalidInput');
    }
  }

  /**
   * Processes a single validation constraint and returns corresponding Angular validators.
   * @param constraint - The constraint object containing validation rules and error messages
   * @param formGroup - Optional form group for cross-field validation
   * @returns A validator function or array of validator functions
   */
  private getValidator(constraint: IInputConstraint, formGroup?: FormGroup): ValidatorFn | ValidatorFn[] {
    // Convert the regex string to a RegExp object (double backslashes because of string-format)
    const regEx = constraint.regex ? new RegExp(constraint.regex.pattern.replace(/\\\\/g, '\\')) : InputValidationPatterns[constraint.type];

    switch (constraint.type) {
      case 'InputConstraintRequired':
        return this.getRequiredValidator();
      case 'InputConstraintMaxLength':
        return constraint.maxLength ? Validators.maxLength(constraint.maxLength) : [];
      case 'InputConstraintMinLength':
        return constraint.minLength ? Validators.minLength(constraint.minLength) : [];
      case 'InputConstraintRegex':
        return this.getRegExValidator(regEx);
      case 'InputConstraintEMail':
        return this.getEmailValidator(regEx);
      case 'InputConstraintOnlyLetters':
        return this.getRegExValidator(regEx);
      case 'InputConstraintContainsValidZIP':
        return this.getRegExValidator(regEx);
      case 'InputConstraintPassword':
        return this.getRegExValidator(regEx);
      case 'InputConstraintLengthMinMax': {
        const minMaxValidators: ValidatorFn[] = [];
        if (constraint.minLength) {
          minMaxValidators.push(Validators.minLength(constraint.minLength));
        }
        if (constraint.maxLength) {
          minMaxValidators.push(Validators.maxLength(constraint.maxLength));
        }
        return minMaxValidators;
      }
      case 'InputConstraintMin':
        return constraint.min != null ? Validators.min(constraint.min) : [];
      case 'InputConstraintMax':
        return constraint.max != null ? Validators.max(constraint.max) : [];
      case 'InputConstraintRepeat':
        return this.getRepeatValidator(constraint.otherInputField, formGroup);
      case 'InputConstraintDateRange':
        return this.getDateRangeValidator(constraint.minDate, constraint.maxDate);
      case 'InputConstraintBlock':
        return this.getBlockRequiredValidator(constraint.blockFields, formGroup);
      default:
        return [];
    }
  }

  /**
   * Returns a validator that checks if the input is filled. A translation value (`{ "<ISO>": "<Text>" }`)
   * (a plain object, unlike e.g. a `Date`) counts as filled once any language has a text, mirroring the backend's `IsEmpty` of `AddComplexTranslation`.
   * @returns A validator function that emits `{ required: true }` on failure.
   */
  private getRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      if (value == null || Object.getPrototypeOf(value) !== Object.prototype) return Validators.required(control);
      return Object.values(value).some((text) => !!text) ? null : { required: true };
    };
  }

  /**
   * Returns a validator that checks if the input is a valid e-mail address.
   * Uses the configured pattern from InputValidationPatterns (falling back to Angular's Validators.email
   * if no pattern is supplied) and surfaces failures under the `email` error key, matching the mapping
   * in `constraintTypeToErrorKey` so the validation message is actually rendered.
   * Empty values pass (let `InputConstraintRequired` handle emptiness).
   * @param regEx - The regular expression to validate against.
   * @returns A validator function that emits `{ email: true }` on failure.
   */
  private getEmailValidator(regEx?: RegExp): ValidatorFn {
    if (!regEx) return Validators.email;
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = this.getControlValue(control);
      if (!value) return null;
      return regEx.test(value) ? null : { email: true };
    };
  }

  /**
   * Returns a validator function that checks if the input matches a specific regex pattern.
   * @param regEx - The regular expression to validate against.
   * @returns A validator function that checks the input against the regex pattern.
   * If no regex is provided, it returns a null validator.
   */
  private getRegExValidator(regEx?: RegExp): ValidatorFn {
    if (!regEx) return Validators.nullValidator;
    return Validators.pattern(regEx);
  }

  /**
   * Returns a validator function that checks if the input value matches another field's value.
   * @param otherFieldName - The name of the other form control to compare against.
   * @param formGroup - The form group containing both fields.
   * @returns A validator function that checks if both field values match.
   */
  private getRepeatValidator(otherFieldName?: string, formGroup?: FormGroup): ValidatorFn {
    if (!otherFieldName || !formGroup) {
      return Validators.nullValidator;
    }

    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const otherControl = formGroup.get([otherFieldName]);
      if (!otherControl) return null;

      const currentValue = this.getControlValue(control);
      const otherValue = this.getControlValue(otherControl);

      if (currentValue !== otherValue) {
        return { repeat: true };
      }
      return null;
    };
  }

  /**
   * Returns a validator that checks if the input date is within a min/max date range.
   * Emits distinct error keys so the validation message can append a directional hint:
   * `dateRange` = unparsable input, `dateRangeMin` = before min, `dateRangeMax` = after max.
   * @param minDate - The minimum allowed date.
   * @param maxDate - The maximum allowed date.
   * @returns A validator function that emits `{ dateRange | dateRangeMin | dateRangeMax: true }` on failure.
   */
  private getDateRangeValidator(minDate?: Date, maxDate?: Date): ValidatorFn {
    // Pre-parse min/max once to avoid repeated parsing on every validation run.
    // Normalize to local-midnight date-only parts to avoid UTC off-by-one issues.
    const parsedMin = minDate ? DateService.toDateOnly(minDate) : undefined;
    const parsedMax = maxDate ? DateService.toDateOnly(maxDate) : undefined;

    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = this.getControlValue(control);
      if (!value) return null;
      const date = DateService.toDateOnly(value);
      if (isNaN(date.getTime())) return { dateRange: true };
      if (parsedMin && date < parsedMin) return { dateRangeMin: true };
      if (parsedMax && date > parsedMax) return { dateRangeMax: true };
      return null;
    };
  }

  /**
   * Returns a validator for an `InputConstraintBlock` — the backend's "at least one of these fields"
   * group, produced by `InputConstraintCollection.CombineRequired`. It strips the per-field required
   * constraint, so without this the whole group ends up with no validator at all.
   * @param blockFields - The names of the inputs sharing the block.
   * @param formGroup - The form group holding those controls.
   * @returns A validator emitting `{ blockRequired: true }` while every field of the group is empty.
   */
  private getBlockRequiredValidator(blockFields?: string[], formGroup?: FormGroup): ValidatorFn {
    if (!blockFields?.length || !formGroup) return Validators.nullValidator;
    // All controls exist before validators are generated, so every block field resolves here.
    return (): { [key: string]: boolean } | null => {
      const filled = blockFields.some((name) => {
        const sibling = formGroup.get([name]);
        if (!sibling) return false;
        // Checkboxes hold booleans — an unchecked one would count as filled through `String(false)`.
        if (typeof sibling.value === 'boolean') return sibling.value;
        return String(this.getControlValue(sibling)).trim().length > 0;
      });
      return filled ? null : { blockRequired: true };
    };
  }

  /**
   * Extracts the value from a form control, handling IInputOption objects.
   * @param control - The form control to extract the value from.
   * @returns The string value of the control.
   */
  private getControlValue(control: AbstractControl): string {
    const value = control.value;
    if (value && typeof value === 'object' && 'value' in value) {
      return (value as IInputOption).value ?? '';
    }
    return value ?? '';
  }
}
